"use client";

import { useEffect, useRef, useState } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

/** Keep in sync with `.cc-seo-cases__draft` transition in `_seo-page.scss`. */
const DRAFT_MOTION_MS = 320;

type CcSeoCasesDraftBadgeProps = {
  label: string;
  visible: boolean;
};

/**
 * Draft pill on SEO case cards — CSS `@starting-style` handles enter; this
 * component keeps the node mounted through the exit transition.
 */
export function CcSeoCasesDraftBadge({
  label,
  visible,
}: CcSeoCasesDraftBadgeProps) {
  const prefersReducedMotion = useCcPrefersReducedMotion();
  const [mounted, setMounted] = useState(visible);
  const [exiting, setExiting] = useState(false);
  const badgeRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (visible) {
      setExiting(false);
      setMounted(true);
      return;
    }

    if (!mounted) return;

    if (prefersReducedMotion) {
      setMounted(false);
      setExiting(false);
      return;
    }

    setExiting(true);
  }, [visible, prefersReducedMotion, mounted]);

  useEffect(() => {
    if (!exiting) return;

    const el = badgeRef.current;
    const finish = () => {
      setMounted(false);
      setExiting(false);
    };

    if (!el) {
      finish();
      return;
    }

    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== el || event.propertyName !== "opacity") return;
      finish();
    };

    el.addEventListener("transitionend", onTransitionEnd);
    const fallback = window.setTimeout(finish, DRAFT_MOTION_MS + 80);

    return () => {
      el.removeEventListener("transitionend", onTransitionEnd);
      window.clearTimeout(fallback);
    };
  }, [exiting]);

  if (!mounted) return null;

  return (
    <span
      ref={badgeRef}
      className={`cc-seo-cases__draft${exiting ? " is-exiting" : ""}`}
    >
      {label}
    </span>
  );
}
